import { useState, type ReactNode } from 'react';
import type { Variant } from '../shared/room';
import { PlayingCard } from '../components/PlayingCard';
import { emptyDraft, resetDraft } from '../lib/draft';
import { navigate } from '../lib/router';

/** The four aces, fanned like a hand of cards. */
const FAN = [
  { card: 'AS', rotate: -21, x: -54, y: 10 },
  { card: 'AH', rotate: -7, x: -18, y: 0 },
  { card: 'AD', rotate: 7, x: 18, y: 0 },
  { card: 'AC', rotate: 21, x: 54, y: 10 },
] as const;

function ModeTile({ icon, title, body, tone, onClick }: { icon: ReactNode; title: string; body: string; tone: 'light' | 'dark'; onClick: () => void }) {
  const light = tone === 'light';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left transition active:scale-[0.99] ${
        light
          ? 'bg-cream text-ink shadow-[0_12px_30px_-14px_rgb(0_0_0/0.8)] hover:bg-white'
          : 'bg-white/[0.06] text-cream ring-1 ring-white/15 hover:bg-white/[0.1] hover:ring-gold/50'
      }`}
    >
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-xl ${light ? 'bg-ink text-gold-soft' : 'bg-gold/15 text-gold-soft ring-1 ring-gold/30'}`}
        aria-hidden
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base leading-tight font-extrabold">{title}</span>
        <span className={`mt-0.5 block text-xs ${light ? 'text-ink-soft' : 'text-cream/65'}`}>{body}</span>
      </span>
      <span className={`text-lg transition-transform group-hover:translate-x-0.5 ${light ? 'text-ink/40' : 'text-gold-soft/70'}`} aria-hidden>
        →
      </span>
    </button>
  );
}

const CardsIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="5" width="10" height="14" rx="2" transform="rotate(-10 8.5 12)" />
    <rect x="10" y="4" width="10" height="14" rx="2" transform="rotate(8 15 11)" />
    <path d="M15 9.2l1.3 1.8-1.3 1.8-1.3-1.8z" fill="currentColor" stroke="none" transform="rotate(8 15 11)" />
  </svg>
);

const OnlineIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
    <path d="M4.5 9.5a11 11 0 0 1 15 0" />
    <path d="M7.5 12.8a6.5 6.5 0 0 1 9 0" />
    <path d="M10.4 16a2.2 2.2 0 0 1 3.2 0" />
    <circle cx="12" cy="18.6" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    <circle cx="12" cy="15.2" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

/** App entry: score a real-life table (offline) or play the cards online. */
export function Welcome() {
  const [step, setStep] = useState<'home' | 'online'>('home');
  const startOnline = (variant: Variant) => {
    resetDraft({ ...emptyDraft('online'), variant });
    navigate('/new');
  };
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
      <div
        className="animate-pop relative overflow-hidden rounded-[2rem] p-[1.5px] shadow-[0_40px_90px_-30px_rgb(0_0_0/0.9)]"
        style={{ background: 'linear-gradient(160deg, rgb(236 212 143 / 0.75), rgb(236 212 143 / 0.08) 35%, rgb(236 212 143 / 0.05) 65%, rgb(236 212 143 / 0.55))' }}
      >
        <div
          className="relative overflow-hidden rounded-[calc(2rem-1.5px)] px-6 pt-9 pb-6 text-center text-cream"
          style={{
            background:
              'radial-gradient(120% 70% at 50% 0%, rgb(124 92 232 / 0.35), transparent 60%), radial-gradient(90% 60% at 50% 110%, rgb(212 82 150 / 0.22), transparent 60%), linear-gradient(180deg, #1c1733 0%, #120f22 100%)',
          }}
        >
          {/* A soft gold glow behind the fan. */}
          <div className="pointer-events-none absolute top-6 left-1/2 h-40 w-64 -translate-x-1/2 rounded-full bg-gold/20 blur-3xl" aria-hidden />

          <div className="relative mx-auto h-[118px] w-[220px]" aria-hidden>
            {FAN.map((f, i) => (
              <span
                key={f.card}
                className="absolute top-0 left-1/2"
                style={{ transform: `translateX(calc(-50% + ${f.x}px)) translateY(${f.y}px) rotate(${f.rotate}deg)`, transformOrigin: '50% 120%' }}
              >
                <span className="animate-rise block" style={{ animationDelay: `${i * 70}ms` }}>
                  <PlayingCard card={f.card} size="lg" />
                </span>
              </span>
            ))}
          </div>

          <h1 className="relative mt-5 font-display text-5xl leading-none font-extrabold tracking-tight">Rang</h1>
          <div className="relative mx-auto mt-4 flex items-center justify-center gap-3 text-gold-soft/60" aria-hidden>
            <span className="h-px w-10 bg-gradient-to-r from-transparent to-gold-soft/50" />
            <span className="text-xs tracking-[0.4em]">
              ♠ <span className="text-red-300/80">♥</span> ♣ <span className="text-red-300/80">♦</span>
            </span>
            <span className="h-px w-10 bg-gradient-to-l from-transparent to-gold-soft/50" />
          </div>
          <p className="relative mx-auto mt-4 max-w-xs text-sm leading-relaxed text-cream/75">Call Rang, run, and keep every Guaa honest.</p>

          {step === 'home' ? (
            <div key="home" className="animate-rise relative mt-7 space-y-2.5">
              <ModeTile icon={<CardsIcon />} title="Offline game" body="Playing with a real deck — keep the score here." tone="light" onClick={() => navigate('/home')} />
              <ModeTile icon={<OnlineIcon />} title="Online game" body="Four phones, cards dealt for you, with voice chat." tone="dark" onClick={() => setStep('online')} />
            </div>
          ) : (
            <div key="online" className="animate-rise relative mt-7 space-y-2.5">
              <p className="eyebrow text-[10px] text-gold-soft/80">Online game · choose the game</p>
              <ModeTile
                icon={<span className="font-display text-lg font-extrabold">♠</span>}
                title="Open Rang"
                body="Rang is shown to everyone. Guaa scoring, fewest Guaa wins."
                tone="light"
                onClick={() => startOnline('open')}
              />
              <ModeTile
                icon={<LockIcon />}
                title="Bandh Rang"
                body="Rang stays hidden until revealed. Run −26, stopped +13 — first to 100 loses."
                tone="dark"
                onClick={() => startOnline('bandh')}
              />
              <button type="button" onClick={() => setStep('home')} className="mx-auto mt-1 block rounded-full px-3 py-1.5 text-xs font-semibold text-cream/60 hover:text-cream">
                ← Back
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
